import { FunnelIcon } from '@heroicons/react/24/outline';
import { useLocalStorage } from '@uidotdev/usehooks';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';
import { OverviewResponse } from '../*';
import { useFetcher, useSelectedCustomer } from '../hooks';
import { normalizeClubList } from '../utils/clubList';
import Modal from './Modal';

export const ClubFilter = () => {
  const selectedCustomer = useSelectedCustomer();
  const fetcher = useFetcher();

  const [storedClubs, setStoredClubs] = useLocalStorage(`clubList-${selectedCustomer?.seoTitle}`, []);
  const { clubs: allClubs, isValid: clubListIsValid } = useMemo(
    () => normalizeClubList(storedClubs),
    [storedClubs],
  );
  const [modalOpen, setModalOpen] = useState(false);
  const filteredClubs = useMemo(() => allClubs.filter((club) => !club.hidden), [allClubs]);
  const sortedClubs = useMemo(
    () => [...allClubs].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de')),
    [allClubs],
  );

  useEffect(() => {
    if (!clubListIsValid) {
      setStoredClubs([]);
    }
  }, [clubListIsValid, setStoredClubs]);

  useSWRImmutable(
    selectedCustomer
      ? `/statsapp/overview?${new URLSearchParams({
          start: dayjs().startOf('day').format('YYYY-MM-DD'),
          dataManagerID: selectedCustomer.dataManagerID,
          seoTitle: selectedCustomer.seoTitle,
        }).toString()}`
      : null,
    fetcher<OverviewResponse>,
    {
      onSuccess: (data) => {
        const freshClubs = normalizeClubList(data?.clubs).clubs;

        setStoredClubs((old) => {
          const previousClubs = normalizeClubList(old).clubs;
          return freshClubs.map((club) => ({
            ...club,
            hidden:
              previousClubs.find(
                ({ clubID, backendID }) => clubID === club.clubID && backendID === club.backendID,
              )?.hidden ??
              false,
          }));
        });
      },
    },
  );

  return (
    <>
      <a
        onClick={() => setModalOpen(true)}
        className="flex gap-2 items-center text-gray-400 text-sm select-none cursor-pointer"
      >
        {filteredClubs?.length} / {allClubs?.length}
        <FunnelIcon className="w-4 h-4" />
      </a>

      <Modal isOpen={modalOpen} setIsOpen={setModalOpen} headline="Clubs filtern">
        <div className="flex flex-col gap-1">
          {sortedClubs.map((club) => {
            const id = club.clubID;
            const backend = club.backendID;

            return (
              <label key={backend + id} className="flex gap-3 items-center text-sm select-none cursor-pointer">
                <input
                  onChange={({ target: { checked } }) => {
                    setStoredClubs((old) => {
                      const list = normalizeClubList(old).clubs.map((c) => {
                        if (c.clubID === club.clubID && c.backendID === club.backendID) {
                          return { ...c, hidden: !checked };
                        }
                        return c;
                      });

                      if (list.every(({ hidden }) => hidden)) {
                        return list.map((c) => ({ ...c, hidden: false }));
                      }
                      return list;
                    });
                  }}
                  checked={!allClubs.find(({ clubID, backendID }) => clubID === id && backendID === backend)?.hidden}
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                {club.name}
              </label>
            );
          })}
        </div>
      </Modal>
    </>
  );
};
